import { useEffect, useState } from "react"
import dayjs from "dayjs"
import LeakHeatmapCalendar from "@/components/detail-modal/leak-heatmap-calendar"
import PeriodSettingMobile from "@/components/detail-modal/period-setting-mobile"
import { usageChartStore } from "../usage-chart-store"
import { periodStore } from "@/components/detail-modal/period-store"
import { aptSearchStore } from "@/routes/select/apt/apt-search-store"
import { calcStartDate } from "@/utils/calc-start-date-util"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { useSearchParams } from "react-router-dom"
import { RESOURCE_META, RESOURCES } from "@/constants/resource-const"
import { usageChartBundle } from "../usage-const"
import UsageMonthDetail from "./usage-month-detail"
import Wrapper from "@/components/wrapper"

const UsageCalendarView = ({ className = "" }) => {
  const { lang } = langStore()
  const [searchParams] = useSearchParams()

  const resource = searchParams.get("resource") ?? "water"
  const dong = searchParams.get("dong")
  const ho = searchParams.get("ho")

  const { ended_at } = aptSearchStore((s) => s.pickApt)

  const { rawXLabels, series, apiGetUsage, unit } = usageChartStore()
  const { startDate, endDate, setStartDate, setEndDate, setActive } = periodStore()

  const [selectedMonthDate, setSelectedMonthDate] = useState(null)

  useEffect(() => {
    if (!ho || !ended_at) return

    if (!startDate) {
      const oneMonth = calcStartDate(ended_at, "months")
      const start = dayjs(oneMonth).startOf("month").format("YYYY-MM-DD")

      setStartDate(start)
    }

    if (!endDate) {
      setEndDate(ended_at)
    }
  }, [ended_at, endDate, ho, setEndDate, setStartDate, startDate])

  const resourceLabel = RESOURCES.find(({ id }) => id === resource)?.label?.[lang] ?? resource

  const resourceColor = RESOURCE_META[resource]?.colors?.primary ?? "#0EA5E9"

  const usageValues = series[0]?.data ?? []
  const usageByDate = {}
  let maxVal = 0

  rawXLabels.forEach((label, index) => {
    const dateKey = label.slice(0, 10)
    const value = Number(usageValues[index]) || 0

    usageByDate[dateKey] = (usageByDate[dateKey] ?? 0) + value

    if (usageByDate[dateKey] > maxVal) {
      maxVal = usageByDate[dateKey]
    }
  })

  const startLabel = rawXLabels[0]?.slice(0, 10) ?? null
  const endLabel = rawXLabels[rawXLabels.length - 1]?.slice(0, 10) ?? null
  const dailyData = usageByDate
  const maxDailyVal = maxVal

  const handlePeriodSearch = async (period = {}) => {
    if (!dong || !ho) return

    setActive(null)
    setSelectedMonthDate(null)

    await apiGetUsage({
      dong,
      ho,
      startDate: period.startDate ?? startDate,
      endDate: period.endDate ?? endDate,
      resource
    })
  }

  return (
    <Wrapper.shadow className={`md:hidden flex flex-col gap-4 ${className}`}>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          {formatMessage(usageChartBundle.title[lang], { resource: resourceLabel })}
        </h2>

        {startLabel && endLabel ? (
          <p className="mt-1 text-sm text-gray-400">
            {dayjs(startLabel).format("YYYY.MM.DD")} ~ {dayjs(endLabel).format("YYYY.MM.DD")}
          </p>
        ) : (
          <p className="mt-1 text-sm text-gray-400">{usageChartBundle.noSelectionMessage[lang]}</p>
        )}
      </div>

      {!selectedMonthDate ? (
        <>
          <PeriodSettingMobile bundle={usageChartBundle} onPeriodSearch={handlePeriodSearch} />

          <div className="min-h-60">
            <LeakHeatmapCalendar
              startDate={startLabel}
              endDate={endLabel}
              dailyData={dailyData}
              maxVal={maxDailyVal}
              color={resourceColor}
              onMonthClick={setSelectedMonthDate}
            />
          </div>
        </>
      ) : (
        <UsageMonthDetail
          date={selectedMonthDate}
          onBack={() => setSelectedMonthDate(null)}
          dailyData={dailyData}
          maxVal={maxDailyVal}
          series={series}
          rawXLabels={rawXLabels}
          unit={unit}
          lang={lang}
          resourceLabel={resourceLabel}
          resourceColor={resourceColor}
          endedAt={ended_at}
        />
      )}
    </Wrapper.shadow>
  )
}

export default UsageCalendarView
